import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Calendar from 'react-calendar';
import type { CalendarType } from 'react-calendar';
import { getThisMonthHolidays } from '../../utils/time';
import { useUser } from '@/hooks/UserContext';
import './ActivityCalendar.scss';
import {
    getActivityInfoFromLs,
    getActivityInfoFromDb,
    ActivitySet,
    convertApiDataToActivitySets,
    filterVisibleActivities,
    validateKeywordForm,
} from './util/ActivityCalendar';

import { ActivityCalendarData } from '@/types/ActivityCalendarType';
import Modal from '@/components/Modal';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import Select from '@/components/Select';
import Alert from '@/components/Alert';
import { apiPostRequest } from '@/utils/apiClient';
import { useTranslation } from 'react-i18next';
import { useResponsiveFontSize } from '@/hooks/useResponsiveFontSize';
import {
    ColorOption,
    getBaseColorOptionsByProvider,
    applyColorOrderWithDividers,
    getDefaultColorOption,
} from '@/utils/colorOptions';
import { LocalStorageKeys } from '@/constants/localStorage';
import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import { buildLanguageTagFromUser } from '@/utils/locale';
import { resolveDateLocale } from '@/i18n/dateFormats';
import { resolveWeekStart } from '@/i18n/weekStart';
import { usePanelLoading } from '@/components/PanelLoadingBoundary';
import Tooltip from '@/components/Tooltip';
import HoverMenuButton from '@/components/menu/HoverMenuButton';
import { buildPanelMenuItems } from '@/components/menu/panelMenu';
import { EditIcon } from '@/components/icons/EditIcon';
import { FullscreenLockedModal } from '@/components/FullscreenLockedModal';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';
import { logActivityViewEvent } from '@/utils/customAnalysisEventLog';
import { IndividualCalendarItem, createIndividualCalendarOptions, isHolidayOrBirthdayCalendar } from '@/features/analysis/seach/util/Search';
import { apiGetRequest } from '@/utils/apiClient';
import { ExternalLinkIcon } from '@/components/icons/ExternalLinkIcon';
import PanelLoader from '@/components/PanelLoader';

type SimpleCalendarProps = {
    onEnterFullScreen?: () => void;
    onExitFullScreen?: () => void;
    isFullScreen?: boolean;
};

/**
 * アクティビティカレンダー
 */
const SimpleCalendar: React.FC<SimpleCalendarProps> = ({
    onEnterFullScreen,
    onExitFullScreen,
    isFullScreen = false,
}) => {
    // レスポンシブ対応
    const containerRef = useRef<HTMLDivElement>(null);
    // フォントサイズ
    const fontSize = useResponsiveFontSize(containerRef, 24);
    const [holidayDates, setHolidayDates] = useState<Date[]>([]);
    // DB そのままの全アクティビティ
    const [allActivities, setAllActivities] = useState<ActivitySet[]>([]);
    const [activityDates, setActivityDates] = useState<Date[]>([]);
    const [activityTimeMap, setActivityTimeMap] = useState<Record<string, number>>({});
    const [currentDisplayName, setCurrentDisplayName] = useState<string>('');
    // 編集モーダル表示状態
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    // フォーム用 state（最大3件分）
    const [keywordForm, setKeywordForm] = useState<
        {
            displayName: string;
            searchKeyword: string;
            color?: string;
        }[]
    >([]);
    // カレンダーリスト
    const [calendarList, setCalendarList] = useState<IndividualCalendarItem[]>([]);
    // カレンダーリスト読み込み中
    const [isCalendarLoading, setIsCalendarLoading] = useState<boolean>(true);
    // 選択されたカレンダーID（全キーワード共通）
    const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');

    // 祝日・誕生日カレンダーを除外したカレンダーリスト
    const filteredCalendarList = useMemo(() => {
        return calendarList.filter((cal) => !isHolidayOrBirthdayCalendar(cal));
    }, [calendarList]);

    // カレンダーが1つだけかどうか
    const isSingleCalendar = filteredCalendarList.length === 1;
    // プランロック用モーダル
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    // 登録処理中
    const [isRegistering, setIsRegistering] = useState<boolean>(false);
    // エラーメッセージキー
    const [errorKey, setErrorKey] = useState<string | null>(null);
    // UserContext からユーザー情報
    const { userInfo } = useUser();
    const { setLoading } = usePanelLoading();
    const [isPanelLoading, setIsPanelLoading] = useState<boolean>(true);

    // 有料プラン判定
    const isPaidPlan = userInfo?.isPaid === true;
    const [isFullscreenLockedModalOpen, setIsFullscreenLockedModalOpen] = useState(false);

    // 色オプション（並び順・仕切り付き）
    const [colorOptionsView, setColorOptionsView] = useState<ColorOption[]>([]);

    // デフォルトのカレンダープロバイダーID
    const providerId = userInfo?.calendarList?.[0]?.providerId || '';
    const providerName = userInfo?.calendarList?.[0]?.providerName || '';
    const { t, i18n } = useTranslation('activityCalendar');
    const { t: tCommon } = useTranslation('common');

    const localizeColorOption = useCallback(
        (option: ColorOption): ColorOption => {
            if (option.isDivider) return option;
            if (option.value === '') {
                return {
                    ...option,
                    label: t('color.none'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            return {
                ...option,
                originalLabel: option.originalLabel ?? option.label,
            };
        },
        [t],
    );

    const languageTag = buildLanguageTagFromUser(userInfo, i18n.language);
    const fixedT = i18n.getFixedT(languageTag, 'activityCalendar');
    const displayNameValues = fixedT('form.placeholders.displayName', { returnObjects: true });
    const keywordValues = fixedT('form.placeholders.searchKeyword', { returnObjects: true });
    const displayNamePlaceholders = Array.isArray(displayNameValues)
        ? (displayNameValues as string[])
        : [];
    const keywordPlaceholders = Array.isArray(keywordValues) ? (keywordValues as string[]) : [];
    const calendarLocale = resolveDateLocale(languageTag);
    const weekStart = resolveWeekStart(userInfo?.weekStart);
    const calendarType: CalendarType = weekStart === 'sun' ? 'gregory' : 'iso8601';

    // 色オプションの並び順を更新（アクティビティでは「すべて」を除外）
    const refreshColorOptions = useCallback(async () => {
        const base = await getBaseColorOptionsByProvider(providerName, languageTag);
        const userId = userInfo?.userId || '';
        const saved = await getLocalStorage(
            userId,
            `${LocalStorageKeys.COLOR_ORDER}:${providerId}`,
        );
        const order: string[] = saved ? JSON.parse(saved) : [];
        const ordered = applyColorOrderWithDividers(base, order);
        const filtered = ordered.filter((o) => o.value !== 'all' && !o.isDivider);
        setColorOptionsView(filtered.map(localizeColorOption));
    }, [providerId, providerName, userInfo?.userId, localizeColorOption, languageTag]);

    useEffect(() => {
        setColorOptionsView((prev) =>
            prev.map((option) => (option.isDivider ? option : localizeColorOption(option))),
        );
    }, [localizeColorOption]);

    /**
     * アクティビティから空データを除外
     */
    const getVisibleActivities = useCallback(
        () =>
            allActivities.filter(
                (a) =>
                    a.displayName.trim() !== '' &&
                    (a.searchKeyword.trim() !== '' || (a.color || '').trim() !== ''),
            ),
        [allActivities],
    );

    /**
     * メイン処理
     */
    const fetchActivities = useCallback(async () => {
        if (!userInfo) return;
        const userId = userInfo.userId;

        // 祝日を取得
        setHolidayDates(getThisMonthHolidays(languageTag));

        // --- アクティビティデータ取得 ---
        let activitiesData: ActivityCalendarData[] | null = null;

        try {
            // ローカルストレージから取得
            const localData = await getActivityInfoFromLs(userId);
            let needFetchFromDb = true;
            // データがある場合
            if (localData) {
                // データ更新日時を取得
                const storedDate = localData.updateDate.split('T')[0];
                const today = new Date().toISOString().split('T')[0];

                // 当日のデータがローカルにある場合はそのまま使用
                if (storedDate === today) {
                    activitiesData = localData.activities;
                    needFetchFromDb = false;
                }
            }

            // DB から取得が必要な場合（LSにデータがない or 更新日付が異なる）
            if (needFetchFromDb) {
                const dbData = await getActivityInfoFromDb(userId);
                if (dbData) {
                    activitiesData = dbData.activityCalendarData;
                }
            }

            // データ整形
            if (activitiesData) {
                // データ変換
                const activities = convertApiDataToActivitySets(activitiesData);

                setAllActivities(activities);
                // 空データを除外
                const visibleActivities = filterVisibleActivities(activities);

                // 初期表示用データをセット
                if (visibleActivities.length > 0) {
                    setCurrentDisplayName(visibleActivities[0].displayName);
                    setActivityDates(visibleActivities[0].dates);
                    setActivityTimeMap(visibleActivities[0].timeMap);
                } else {
                    setCurrentDisplayName('');
                    setActivityDates([]);
                    setActivityTimeMap({});
                }
            }
        } catch (error) {
            // ログインページにリダイレクト
            window.location.replace('/login');
        }
    }, [languageTag, userInfo]);

    /**
     * メイン処理実行タイミング制御
     * 初回 & userInfo 変化時に実行
     */
    useEffect(() => {
        if (!userInfo) {
            setIsPanelLoading(true);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setIsPanelLoading(true);
            try {
                await fetchActivities();
            } finally {
                if (!cancelled) {
                    setIsPanelLoading(false);
                }
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [fetchActivities, userInfo]);

    /**
     * カレンダーリストを取得
     */
    useEffect(() => {
        const fetchCalendars = async () => {
            setIsCalendarLoading(true);
            try {
                const response = await apiGetRequest('/api/settings/calendars');
                if (!response.ok) {
                    console.error('Failed to fetch calendars:', response.status);
                    return;
                }
                const data = await response.json();
                setCalendarList(data.calendars || []);
            } catch (error) {
                console.error('Error fetching calendars:', error);
            } finally {
                setIsCalendarLoading(false);
            }
        };
        void fetchCalendars();
    }, []);

    /**
     * モーダルを開く直前にフォームに既存値をセット
     */
    useEffect(() => {
        if (!isEditModalOpen) return;

        // 既存のアクティビティデータからcalendarIdを取得するため、DBから再取得
        const fetchExistingData = async () => {
            if (!userInfo) return;
            try {
                const dbData = await getActivityInfoFromDb(userInfo.userId);
        // allActivities から 3 件分の値を取得（足りない分は空文字）
                const formData = Array.from({ length: 3 }).map((_, idx) => {
                    const activity = allActivities[idx];
                    return {
                        displayName: activity?.displayName || '',
                        searchKeyword: activity?.searchKeyword || '',
                        color: activity?.color || '',
                    };
                });
                setKeywordForm(formData);
                
                // カレンダーIDを設定（最初のキーワードのcalendarIdを使用、なければデフォルト）
                const firstActivity = dbData?.activityCalendarData?.[0];
                const calendarId = firstActivity?.calendarId || (() => {
                    // カレンダーが1つだけの場合は自動的にそのカレンダーを使用
                    if (isSingleCalendar && filteredCalendarList[0]) {
                        return String(filteredCalendarList[0].id);
                    }
                    const defaultCalendar = calendarList.find((cal) => cal.isDefault) || calendarList[0];
                    return defaultCalendar ? String(defaultCalendar.id) : '';
                })();
                setSelectedCalendarId(calendarId);
            } catch (error) {
                console.error('Error fetching existing data:', error);
                // エラー時はデフォルト値で初期化
                const calendarIdToSet = (() => {
                    // カレンダーが1つだけの場合は自動的にそのカレンダーを使用
                    if (isSingleCalendar && filteredCalendarList[0]) {
                        return String(filteredCalendarList[0].id);
                    }
                    const defaultCalendar = calendarList.find((cal) => cal.isDefault) || calendarList[0];
                    return defaultCalendar ? String(defaultCalendar.id) : '';
                })();
        const formData = Array.from({ length: 3 }).map((_, idx) => ({
            displayName: allActivities[idx]?.displayName || '',
            searchKeyword: allActivities[idx]?.searchKeyword || '',
            color: allActivities[idx]?.color || '',
        }));
        setKeywordForm(formData);
                setSelectedCalendarId(calendarIdToSet);
            }
        // 色オプション初期化
        (async () => {
            await refreshColorOptions();
        })();
        };
        void fetchExistingData();
    }, [isEditModalOpen, allActivities, refreshColorOptions, calendarList, userInfo]);

    /**
     * 編集ボタン押下
     */
    const handleEditButtonClick = () => {
        // 編集モーダルを表示
        setErrorKey(null);
        setIsEditModalOpen(true);
    };

    /**
     * 登録ボタン押下
     */
    const handleRegisterButtonClick = async () => {
        // エラーメッセージリセット
        setErrorKey(null);

        // 最大登録件数
        const maxItems = isPaidPlan ? 3 : 1;
        // バリデーション
        const validation = validateKeywordForm(keywordForm, maxItems);
        if (!validation.valid) {
            const key = validation.errorKey
                ? `validation.${validation.errorKey}`
                : 'validation.inputError';
            setErrorKey(key);
            return;
        }

        // 登録対象データ
        const keywordsToRegister = validation.keywordsToRegister || [];

        // 登録処理
        setIsRegistering(true);
        try {
            // プロバイダーID（後方互換性のため残す）
            const providerId = userInfo?.calendarList?.[0]?.providerId || '';
            // リクエストボディ
            // すべてのキーワードに同じcalendarIdを設定
            // カレンダーが1つだけの場合は自動的にそのカレンダーを使用
            const calendarIdToUse = selectedCalendarId || (() => {
                if (isSingleCalendar && filteredCalendarList[0]) {
                    return String(filteredCalendarList[0].id);
                }
                const defaultCalendar = calendarList.find((cal) => cal.isDefault) || calendarList[0];
                return defaultCalendar ? String(defaultCalendar.id) : '';
            })();
            const body = {
                userId: userInfo?.userId,
                providerId,
                activityKeywords: keywordsToRegister.map((kw) => ({
                    ...kw,
                    calendarId: calendarIdToUse,
                })),
            };
            // APIリクエスト
            const response = await apiPostRequest('/api/activity-calendar/keywords/register', body);
            // 失敗時
            if (!response?.ok) {
                // バリデーションエラー（400）
                if (response?.status === 400) {
                    setErrorKey('messages.validationError');
                    setIsRegistering(false);
                    return;
                }
                // 認証エラーは apiPostRequest 内で処理済み
                setErrorKey('messages.genericError');
                setIsRegistering(false);
                // ログインページにリダイレクト
                window.location.replace('/login');
                return;
            }

            // 正常: DB から直接最新データを取得（LS キャッシュを回避）
            await refreshActivities();

            // イベントログ送信（登録成功後）
            // 登録したデータから最大3件分の情報を取得
            const activity1 = keywordsToRegister[0];
            const activity2 = keywordsToRegister[1];
            const activity3 = keywordsToRegister[2];
            void logActivityViewEvent({
                hasDisplayName1: Boolean(activity1?.displayName?.trim()),
                hasDisplayName2: Boolean(activity2?.displayName?.trim()),
                hasDisplayName3: Boolean(activity3?.displayName?.trim()),
                hasKeyword1: Boolean(activity1?.searchKeyword?.trim()),
                hasKeyword2: Boolean(activity2?.searchKeyword?.trim()),
                hasKeyword3: Boolean(activity3?.searchKeyword?.trim()),
                color1: activity1?.color || '',
                color2: activity2?.color || '',
                color3: activity3?.color || '',
            });

            // モーダルを閉じる
            setErrorKey(null);
            setIsEditModalOpen(false);
        } catch (error) {
            setErrorKey('messages.genericError');
            // ログインページにリダイレクト
            window.location.replace('/login');
        } finally {
            setIsRegistering(false);
        }
    };

    /**
     * アクティビティデータ取得
     */
    const refreshActivities = async () => {
        if (!userInfo) return;
        setIsPanelLoading(true);
        try {
            const dbData = await getActivityInfoFromDb(userInfo.userId);
            if (!dbData) return;
            const activities = convertApiDataToActivitySets(dbData.activityCalendarData);
            setAllActivities(activities);
            const visibleActivities = filterVisibleActivities(activities);
            if (visibleActivities.length > 0) {
                setCurrentDisplayName(visibleActivities[0].displayName);
                setActivityDates(visibleActivities[0].dates);
                setActivityTimeMap(visibleActivities[0].timeMap);
            } else {
                setCurrentDisplayName('');
                setActivityDates([]);
                setActivityTimeMap({});
            }
        } finally {
            setIsPanelLoading(false);
        }
    };

    useEffect(() => {
        if (!userInfo) {
            setLoading(true);
            return;
        }
        setLoading(isPanelLoading);
    }, [isPanelLoading, setLoading, userInfo]);

    // メニュー項目を構築
    const menuItems = buildPanelMenuItems({
        baseItems: isFullScreen
            ? []
            : [
                  {
                      key: 'edit',
                      label: t('buttons.edit'),
                      icon: <EditIcon />,
                      onClick: handleEditButtonClick,
                  },
              ],
        enableFullscreen: true,
        isFullScreen,
        onEnterFullScreen: isPaidPlan ? onEnterFullScreen : undefined,
        onExitFullScreen,
        enterLabel: tCommon('actions.fullscreenEnter'),
        exitLabel: tCommon('actions.fullscreenExit'),
        isFullscreenLocked: !isPaidPlan,
        fullscreenTooltip: tCommon('actions.fullscreenLockedTooltip'),
        onFullscreenLockedClick: () => setIsFullscreenLockedModalOpen(true),
    });

    // レンダリング
    return (
        <div
            ref={containerRef}
            style={{ fontSize: `${fontSize}px` }}
            className="h-full w-full flex flex-col relative"
        >
            {/* メニューボタン */}
            <div className="absolute top-0 right-0 z-10">
                <HoverMenuButton
                    title={t('buttons.edit')}
                    ariaLabel={t('buttons.edit')}
                    items={menuItems}
                />
            </div>
            {!isPaidPlan && (
                <FullscreenLockedModal
                    isOpen={isFullscreenLockedModalOpen}
                    onClose={() => setIsFullscreenLockedModalOpen(false)}
                    onGoPlans={() => {
                        openPlansWithTracking({
                            source: 'fullscreen_activityCalendar',
                            panelId: 'hmActivityCalendar',
                        });
                    }}
                />
            )}
            {/* キーワードセクション */}
            <div className="flex flex-wrap mt-2 mb-1 flex-shrink-0">
                <div className="w-full text-center">
                    {/* カレンダー情報がある場合 */}
                    <div className="flex flex-wrap justify-center gap-2">
                        {/* キーワードボタン */}
                        {getVisibleActivities().map((activity, index) => (
                            <button
                                key={index}
                                className={`mx-1 text-gray-700 border border-gray-300 rounded-lg px-2 py-1 ${
                                    currentDisplayName === activity.displayName
                                        ? 'border-transparent bg-kotomilThemaColorLight'
                                        : 'bg-white'
                                }`}
                                style={{ fontSize: `0.9em` }}
                                onClick={() => {
                                    setCurrentDisplayName(activity.displayName);
                                    setActivityDates(activity.dates);
                                    setActivityTimeMap(activity.timeMap);
                                }}
                            >
                                {activity.displayName}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* カレンダービューセクション */}
            <div
                style={{ fontSize: `${fontSize}px` }}
                className="w-full flex-1 min-h-0 overflow-hidden"
            >
                <Calendar
                    // クリック無効化
                    onClickDay={() => undefined}
                    // 変更無効
                    onChange={() => undefined}
                    // ナビゲーション非表示
                    showNavigation={false}
                    // 月表示に固定
                    view="month"
                    // 言語
                    locale={calendarLocale}
                    calendarType={calendarType}
                    // 日付の"日"を取り除く
                    formatDay={(_locale: string | undefined, date: Date) => {
                        return date.getDate().toString();
                    }}
                    // 活動日、祝日にクラスを適用
                    tileClassName={({ date, view }: { date: Date; view: string }) => {
                        if (view !== 'month') return null;

                        // 活動日と祝日の両方に対応するようにクラスを適用
                        const classes: string[] = [];

                        const dayOfWeek = date.getDay();
                        if (dayOfWeek === 0) {
                            classes.push('weekday-sunday');
                        } else if (dayOfWeek === 6) {
                            classes.push('weekday-saturday');
                        }

                        // 活動日にクラスを適用
                        const isActivityDate = activityDates.some(
                            (activityDate) =>
                                activityDate.getFullYear() === date.getFullYear() &&
                                activityDate.getMonth() === date.getMonth() &&
                                activityDate.getDate() === date.getDate(),
                        );
                        if (isActivityDate) classes.push('activity-date');

                        // 祝日にクラスを適用
                        const isHolidayDate = holidayDates.some(
                            (holidayDate) =>
                                holidayDate.getFullYear() === date.getFullYear() &&
                                holidayDate.getMonth() === date.getMonth() &&
                                holidayDate.getDate() === date.getDate(),
                        );
                        if (isHolidayDate) classes.push('holiday-date');
                        return classes.join(' ');
                    }}
                    // ツールチップ（データがある日のみ表示）
                    tileContent={({ date, view }: { date: Date; view: string }) => {
                        if (view !== 'month') return null;

                        // 日付をキーにして活動時間を取得
                        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
                            2,
                            '0',
                        )}-${String(date.getDate()).padStart(2, '0')}`;
                        const minutes = activityTimeMap[key];
                        if (!minutes) return null;

                        const hours = Math.floor(minutes / 60);
                        const mins = minutes % 60;
                        const parts: string[] = [];
                        if (hours > 0) parts.push(t('tooltip.hours', { count: hours }));
                        if (mins > 0) parts.push(t('tooltip.minutes', { count: mins }));
                        const timeStr = parts.join(' ').trim();
                        if (!timeStr) return null;

                        // Radix Tooltip を利用
                        return (
                            <Tooltip content={timeStr}>
                                <span
                                    className="absolute inset-0 z-20 block hover:bg-none"
                                    style={{ pointerEvents: 'auto', cursor: 'default' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                />
                            </Tooltip>
                        );
                    }}
                    className="w-full h-full border-0 rounded py-0 px-2 lg:px-6"
                />
            </div>

            {/* 追加／編集モーダル */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setErrorKey(null);
                }}
                contentClassName="p-6 w-full max-w-xl"
            >
                <h2 className="text-lg font-bold mb-6 text-center tracking-widest leading-relaxed">
                    {t('title')}
                </h2>
                
                {/* カレンダー選択欄（全キーワード共通、一番上に表示） */}
                {/* 祝日カレンダーを除外してカレンダーが1つだけの場合は非表示 */}
                {!isSingleCalendar && (
                    <div className="flex justify-center items-center border-b p-4 pb-6">
                        <div className="flex justify-center gap-1">
                            <div className="flex flex-col justify-center gap-4">
                                <label htmlFor="calendar-select">
                                    {t('form.labels.calendar') || 'カレンダー'}
                                </label>
                            </div>
                            <div className="flex flex-col justify-center gap-2 min-w-[200px]">
                                {isCalendarLoading || calendarList.length === 0 || (isEditModalOpen && !selectedCalendarId && !isSingleCalendar) ? (
                                    <div className="flex items-center justify-center h-8">
                                        <PanelLoader label="" className="py-0" />
                                    </div>
                                ) : (
                                    <Select
                                        options={createIndividualCalendarOptions(calendarList, { lockNonDefault: !isPaidPlan })}
                                        defaultValue={(() => {
                                            if (!selectedCalendarId) {
                                                const defaultCalendar = calendarList.find((cal) => cal.isDefault) || calendarList[0];
                                                return defaultCalendar ? { value: String(defaultCalendar.id), label: defaultCalendar.calendarName } : { value: '', label: '' };
                                            }
                                            const calendar = calendarList.find((cal) => String(cal.id) === selectedCalendarId);
                                            return calendar ? { value: String(calendar.id), label: calendar.calendarName } : { value: selectedCalendarId, label: selectedCalendarId };
                                        })()}
                                        onValueChange={(option) => {
                                            if (option.isLocked) {
                                                setIsPlanModalOpen(true);
                                                return;
                                            }
                                            setSelectedCalendarId(option.value);
                                        }}
                                        className="h-8 min-w-[200px]"
                                        noWrap={true}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                
                {/* 表示名と検索キーワード入力欄 */}
                {/* ３つ作成し、値があれば表示する */}
                {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="flex justify-center items-center border-b p-4">
                        <div className="text-center text-gray-400 text-3xl pe-10">{index + 1}</div>
                        <div className="flex justify-center gap-1">
                            <div className="flex flex-col justify-center gap-4">
                                <label htmlFor={`displayName-${index}`}>
                                    {t('form.labels.displayName')}
                                </label>
                                <label htmlFor={`searchKeyword-${index}`}>
                                    {t('form.labels.searchKeyword')}
                                </label>
                                <label htmlFor={`color-${index}`}>{t('form.labels.color')}</label>
                            </div>
                            <div className="flex flex-col justify-center gap-2">
                                <Input
                                    type="text"
                                    id={`displayName-${index}`}
                                    className="h-8"
                                    showClearButton={true}
                                    placeholder={displayNamePlaceholders[index] || ''}
                                    value={keywordForm[index]?.displayName || ''}
                                    onChange={(e) => {
                                        const newForm = [...keywordForm];
                                        newForm[index].displayName = e.target.value;
                                        setKeywordForm(newForm);
                                    }}
                                    disabled={!isPaidPlan && index > 0}
                                />
                                <Input
                                    type="text"
                                    id={`searchKeyword-${index}`}
                                    className="h-8"
                                    showClearButton={true}
                                    placeholder={keywordPlaceholders[index] || ''}
                                    value={keywordForm[index]?.searchKeyword || ''}
                                    onChange={(e) => {
                                        const newForm = [...keywordForm];
                                        newForm[index].searchKeyword = e.target.value;
                                        setKeywordForm(newForm);
                                    }}
                                    disabled={!isPaidPlan && index > 0}
                                />
                                <Select
                                    options={colorOptionsView}
                                    defaultValue={(() => {
                                        const v = keywordForm[index]?.color || '';
                                        if (!v) return localizeColorOption(getDefaultColorOption());
                                        const found = colorOptionsView.find((o) => o.value === v);
                                        return (
                                            found || localizeColorOption(getDefaultColorOption())
                                        );
                                    })()}
                                    onValueChange={(option) => {
                                        if ((option as any).isDivider) return;
                                        if (!isPaidPlan && index > 0) return;
                                        const v = option?.value || '';
                                        const newForm = [...keywordForm];
                                        newForm[index].color = v === 'all' ? '' : v;
                                        setKeywordForm(newForm);
                                        // MRU 保存（個別色のみ）
                                        if (v && v !== 'all') {
                                            (async () => {
                                                const userId = userInfo?.userId || '';
                                                const key = `${LocalStorageKeys.COLOR_ORDER}:${providerId}`;
                                                const saved = await getLocalStorage(userId, key);
                                                let order: string[] = [];
                                                if (saved) {
                                                    try {
                                                        order = JSON.parse(saved);
                                                    } catch {}
                                                }
                                                order = [v, ...order.filter((x) => x !== v)].slice(
                                                    0,
                                                    20,
                                                );
                                                await setLocalStorage(
                                                    userId,
                                                    key,
                                                    JSON.stringify(order),
                                                );
                                                await refreshColorOptions();
                                            })();
                                        }
                                    }}
                                    className={`h-8 min-w-[200px] ${!isPaidPlan && index > 0 ? 'pointer-events-none opacity-50' : ''}`}
                                    dropdownClassName="min-w-[400px] w-[400px]"
                                />
                            </div>
                        </div>
                    </div>
                ))}
                {/* エラーメッセージ */}
                {errorKey && (
                    <div className="mt-4">
                        <Alert
                            type="danger"
                            messages={[t(errorKey)]}
                            className="text-center text-sm py-2"
                        />
                    </div>
                )}
                {/* 非有料プランユーザーメッセージ */}
                {!isPaidPlan && (
                    <div className="mt-4">
                        <Alert
                            type="warning"
                            allowHtml={true}
                            messages={[
                                t('alerts.nonPaid', { interpolation: { escapeValue: false } }),
                            ]}
                            className="text-center text-sm py-2"
                        />
                    </div>
                )}
                {/* 説明 */}
                <div className="flex flex-col gap-2 text-sm text-gray-600 mt-8">
                    <p className="tracking-widest leading-relaxed">
                        {t('descriptions.displayName')}
                    </p>
                    <p className="tracking-widest leading-relaxed">
                        {t('descriptions.searchKeyword')}
                    </p>
                    <p className="tracking-widest leading-relaxed">{t('descriptions.color')}</p>
                </div>

                <div className="mt-12 mb-6 text-center flex justify-center gap-8">
                    <Button
                        variant="outline"
                        className="w-auto"
                        onClick={() => {
                            setIsEditModalOpen(false);
                            setErrorKey(null);
                        }}
                    >
                        {t('buttons.cancel')}
                    </Button>
                    <Button 
                        className="w-full sm:w-auto px-6 py-2 h-10 bg-kotomilThemaColor hover:bg-kotomilThemaColor/80 disabled:opacity-60 whitespace-nowrap" 
                        onClick={handleRegisterButtonClick}
                        disabled={isRegistering}
                    >
                        {isRegistering ? t('buttons.submitting') : t('buttons.register')}
                    </Button>
                </div>
            </Modal>

            {/* プランロックモーダル */}
            {!isPaidPlan && (
                <PlanLockedModal
                    isOpen={isPlanModalOpen}
                    onClose={() => setIsPlanModalOpen(false)}
                    onGoPlans={() =>
                        openPlansWithTracking({
                            source: 'calendar_selection_activity',
                        })
                    }
                />
            )}
        </div>
    );
};

// プランロックモーダルコンポーネント
interface PlanLockedModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGoPlans: () => void;
}

function PlanLockedModal({ isOpen, onClose, onGoPlans }: PlanLockedModalProps) {
    const { t } = useTranslation('analysis');
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            contentClassName="w-full max-w-md p-6"
        >
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center">
                    {t('lockedModal.title')}
                </h3>
                <div
                    className="text-sm text-gray-700 [&_strong]:font-semibold [&_strong]:block [&_strong]:mt-3 [&_strong]:mb-2 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:mt-2 [&_ul]:mb-2 [&_ul]:pl-4 [&_li]:mb-1 [&_li]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: t('lockedModal.message') }}
                />
                <div className="flex justify-center mt-4">
                    <button
                        type="button"
                        className="px-6 py-2 rounded-md bg-kotomilThemaColor text-black hover:bg-kotomilThemaColor/90 text-sm inline-flex items-center gap-2"
                        onClick={() => {
                            onClose();
                            onGoPlans();
                        }}
                    >
                        <span>{t('lockedModal.cta')}</span>
                        <ExternalLinkIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default SimpleCalendar;
